import { apiRequest } from "@/lib/queryClient";

const SIGNED_URL_HOPS = 1;

/**
 * Filename from a Content-Disposition header. Prefers RFC 5987
 * `filename*` and falls back to the quoted `filename` the storage
 * streamer sets (`attachment; filename="…"`).
 */
export function filenameFromContentDisposition(
  header: string | null,
  fallback = "download",
): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (star?.[1]) {
    const raw = star[1].trim().replace(/^"(.*)"$/, "$1");
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded) return decoded;
    } catch {
      if (raw) return raw;
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename\s*=\s*([^;]+)/i.exec(header);
  if (plain?.[1]) {
    const name = plain[1].trim().replace(/^"(.*)"$/, "$1");
    if (name) return name;
  }
  return fallback;
}

/**
 * How a leftover `/api/…` anchor should be fetched.
 *
 * A path segment that is exactly `file`, a `/download` route, a `.pdf`,
 * a `download` attribute, or `download=1` / `download=true` saves the file. Other
 * `target="_blank"` links (including I-956K `/files/revision/…` views)
 * open a preview. `files` is not treated as `file`.
 */
export function authenticatedAnchorMode(
  href: string,
  opts: { hasDownloadAttr?: boolean; target?: string | null } = {},
): "download" | "view" {
  if (opts.hasDownloadAttr) return "download";
  const hashless = href.split("#")[0];
  const path = hashless.split("?")[0];
  const query = hashless.includes("?") ? hashless.slice(hashless.indexOf("?") + 1) : "";
  if (/\/download$/.test(path) || /\.pdf$/i.test(path) || /\/file$/.test(path)) return "download";
  if (/(?:^|&)download=(?:1|true)(?:&|$)/.test(query)) return "download";
  if (opts.target === "_blank") return "view";
  return "download";
}

/**
 * Left click and middle click only. A right-click (`button === 2`) must
 * keep the browser context menu and must not start a transfer.
 */
export function shouldInterceptApiAnchorClick(type: string, button: number): boolean {
  if (type === "click" && button === 0) return true;
  if (type === "auxclick" && button === 1) return true;
  return false;
}

/** Same-origin `/api/…` path. Top-level navigations to these drop session auth. */
export function isSameOriginApiPath(url: string): boolean {
  if (url.startsWith("/api/")) return true;
  if (typeof window === "undefined") return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

function safeApiPath(url: unknown): string | null {
  if (typeof url !== "string") return null;
  if (!url.startsWith("/api/") || url.startsWith("//") || url.includes("://")) return null;
  return url;
}

function isSignedUrlEnvelope(
  requestUrl: string,
  payload: unknown,
): payload is { url: string; fileName: string } {
  if (!/\/download(?:\?|$)/.test(requestUrl.split("#")[0])) return false;
  if (!payload || typeof payload !== "object") return false;
  const record = payload as { url?: unknown; fileName?: unknown };
  return safeApiPath(record.url) != null && typeof record.fileName === "string";
}

/**
 * GET an authenticated file. Uses the same cookie + refresh path as the
 * rest of the app (`apiRequest`), then reads the body. A `/download`
 * endpoint that returns `{ url, fileName }` is followed once; the second
 * request is still authenticated. Never puts a token in the query string.
 */
export async function loadAuthenticatedFile(
  url: string,
  fallbackName = "download",
  hopsLeft = SIGNED_URL_HOPS,
): Promise<{ blob: Blob; fileName: string; contentType: string }> {
  const res = await apiRequest("GET", url);
  const contentType = res.headers.get("Content-Type") ?? "";
  if (hopsLeft > 0 && contentType.includes("application/json")) {
    const text = await res.text();
    let payload: unknown = null;
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = null;
    }
    if (isSignedUrlEnvelope(url, payload)) {
      return loadAuthenticatedFile(payload.url, payload.fileName || fallbackName, hopsLeft - 1);
    }
    const fileName = filenameFromContentDisposition(
      res.headers.get("Content-Disposition"),
      fallbackName,
    );
    return {
      blob: new Blob([text], { type: contentType || "application/json" }),
      fileName,
      contentType: contentType || "application/json",
    };
  }
  const blob = await res.blob();
  const fileName = filenameFromContentDisposition(
    res.headers.get("Content-Disposition"),
    fallbackName,
  );
  return {
    blob,
    fileName,
    contentType: contentType || blob.type || "application/octet-stream",
  };
}

function saveBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
}

export async function downloadAuthenticatedFile(
  url: string,
  fallbackName = "download",
): Promise<void> {
  const { blob, fileName } = await loadAuthenticatedFile(url, fallbackName);
  saveBlob(blob, fileName || fallbackName);
}

export async function viewAuthenticatedFile(
  url: string,
  fallbackName = "download",
): Promise<void> {
  const { blob, fileName, contentType } = await loadAuthenticatedFile(url, fallbackName);
  const typed = blob.type
    ? blob
    : new Blob([await blob.arrayBuffer()], { type: contentType || "application/octet-stream" });
  const objectUrl = URL.createObjectURL(typed);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    saveBlob(typed, fileName || fallbackName);
    URL.revokeObjectURL(objectUrl);
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
